import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, ShieldCheck, AlertTriangle, RefreshCw, CheckCircle2, XCircle, Loader2, Info, User } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Cross-browser getUserMedia
const getMediaStream = async (constraints) => {
  if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
    return navigator.mediaDevices.getUserMedia(constraints);
  }
  // Legacy fallback
  const getUserMedia = navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia || navigator.msGetUserMedia;
  if (!getUserMedia) throw new Error('Camera not supported in this browser');
  return new Promise((resolve, reject) => getUserMedia.call(navigator, constraints, resolve, reject));
};

export default function IdentityVerification({ examId, examName, onVerified, onSkip }) {
  const [step, setStep] = useState('instructions');
  const [stream, setStream] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [cameraReady, setCameraReady] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => { checkVerificationStatus(); }, [examId]);

  const checkVerificationStatus = async () => {
    try {
      setStatusLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/candidate/exams/${examId}/verification-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerificationStatus(res.data);
      if (!res.data.requires_verification) {
        if (res.data.status === 'not_required' || res.data.status === 'no_photo') onSkip();
        else if (res.data.status === 'verified' || res.data.status === 'manually_verified') onVerified();
      }
    } catch (err) {
      setError('Failed to check verification status');
    } finally { setStatusLoading(false); }
  };

  const startCamera = useCallback(async () => {
    try {
      setError(''); setCameraReady(false);
      const mediaStream = await getMediaStream({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
        audio: false
      });
      setStream(mediaStream);
      setStep('camera');
    } catch (err) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Camera permission denied. Please allow camera access in your browser settings.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError('No camera found. Please connect a camera and try again.');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setError('Camera is in use by another application. Close other apps using the camera and try again.');
      } else {
        setError(err.message || 'Unable to access camera. Please check your browser settings.');
      }
    }
  }, []);

  // Attach stream to video element AFTER it renders in the DOM
  useEffect(() => {
    if (step === 'camera' && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.onloadedmetadata = () => {
        videoRef.current.play().then(() => setCameraReady(true)).catch(() => setCameraReady(true));
      };
    }
  }, [step, stream]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedPhoto(dataUrl);
    setStep('preview');
  }, []);

  const retakePhoto = useCallback(() => {
    setCapturedPhoto(null);
    setError('');
    setStep('camera');
  }, []);

  const submitVerification = async () => {
    if (!capturedPhoto) return;
    setLoading(true); setStep('verifying'); setError('');
    try {
      const token = localStorage.getItem('token');
      // Compress the live photo before sending to reduce payload
      const compressedPhoto = await compressImage(capturedPhoto, 480, 0.6);
      const res = await axios.post(
        `${API}/api/candidate/exams/${examId}/verify-identity`,
        { live_photo: compressedPhoto },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      setVerificationResult(res.data);
      setStep('result');
      if (res.data.verified) {
        if (stream) { stream.getTracks().forEach(t => t.stop()); setStream(null); }
        setTimeout(() => onVerified(), 2000);
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 504 || err.code === 'ECONNABORTED' || !err.response) {
        setError('Verification is taking longer than expected. This does not count as an attempt — please try again.');
      } else {
        setError(detail || 'Verification failed. Please try again.');
      }
      setStep(status === 403 ? 'blocked' : 'preview');
    } finally { setLoading(false); }
  };

  // Compress image client-side before upload
  const compressImage = (dataUrl, maxSize, quality) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width, h = img.height;
        if (w > maxSize || h > maxSize) {
          const ratio = Math.min(maxSize / w, maxSize / h);
          w = Math.round(w * ratio);
          h = Math.round(h * ratio);
        }
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = () => resolve(dataUrl); // fallback to original
      img.src = dataUrl;
    });
  };

  // Cleanup camera on unmount
  useEffect(() => {
    return () => { if (stream) stream.getTracks().forEach(t => t.stop()); };
  }, [stream]);

  if (statusLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center" data-testid="verification-loading">
        <Loader2 className="w-7 h-7 sm:w-8 sm:h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  if (verificationStatus?.status === 'blocked') {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4" data-testid="verification-blocked">
        <div className="max-w-lg w-full bg-zinc-900 border border-red-500/30 rounded-2xl p-6 sm:p-8 text-center">
          <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 bg-red-500/10 rounded-full flex items-center justify-center">
            <XCircle className="w-7 h-7 sm:w-8 sm:h-8 text-red-400" />
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-white mb-2">Verification Blocked</h2>
          <p className="text-zinc-400 text-xs sm:text-sm mb-4 sm:mb-6">
            All verification attempts have been used. Please contact your exam organiser for manual verification.
          </p>
          <div className="bg-zinc-800/50 rounded-lg p-3 sm:p-4 text-left">
            <p className="text-xs sm:text-sm text-zinc-500">Exam: <span className="text-zinc-300">{examName}</span></p>
            <p className="text-xs sm:text-sm text-zinc-500 mt-1">Application No: <span className="text-zinc-300">{verificationStatus?.application_number}</span></p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-3 sm:p-4" data-testid="identity-verification">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-4 sm:mb-6">
          <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1 sm:py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-full mb-3 sm:mb-4">
            <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-400" />
            <span className="text-amber-300 text-xs sm:text-sm font-medium">Identity Verification Required</span>
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-white mb-1">Verify Your Identity</h1>
          <p className="text-zinc-400 text-xs sm:text-sm">{examName}</p>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
          {/* Instructions */}
          {step === 'instructions' && (
            <div className="p-5 sm:p-8" data-testid="verification-instructions">
              <div className="flex items-center gap-2.5 sm:gap-3 mb-4 sm:mb-6">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-amber-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Info className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm sm:text-base">Before You Begin</h3>
                  <p className="text-zinc-500 text-[10px] sm:text-sm">Read the following instructions carefully</p>
                </div>
              </div>

              <div className="space-y-2.5 sm:space-y-4 mb-6 sm:mb-8">
                {[
                  { num: '01', title: 'Camera Access', desc: 'You will need to allow camera access. This is mandatory.' },
                  { num: '02', title: 'Face the Camera', desc: 'Position your face clearly. Ensure good lighting and remove obstructions.' },
                  { num: '03', title: 'Photo Comparison', desc: 'Your live photo will be matched with your registered ID photo.' },
                  { num: '04', title: 'Limited Attempts', desc: `You have ${verificationStatus?.attempts_remaining || 3} attempt(s). After all used, contact your organiser.` },
                  { num: '05', title: 'Exam Rules', desc: 'Once verified, the exam starts. Tab switching and suspicious activity will be monitored.' },
                ].map((item) => (
                  <div key={item.num} className="flex gap-3 sm:gap-4 p-2.5 sm:p-3 rounded-lg bg-zinc-800/40 border border-zinc-800">
                    <span className="text-amber-400 font-mono text-xs sm:text-sm font-bold mt-0.5 flex-shrink-0">{item.num}</span>
                    <div className="min-w-0">
                      <p className="text-white text-xs sm:text-sm font-medium">{item.title}</p>
                      <p className="text-zinc-500 text-[10px] sm:text-xs mt-0.5 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {verificationStatus?.candidate_name && (
                <div className="bg-zinc-800/30 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 border border-zinc-700/50">
                  <div className="flex items-center gap-2.5 sm:gap-3">
                    <User className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white text-xs sm:text-sm font-medium truncate">{verificationStatus.candidate_name}</p>
                      <p className="text-zinc-500 text-[10px] sm:text-xs">Application No: {verificationStatus.application_number}</p>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">
                  <p className="text-red-400 text-xs sm:text-sm">{error}</p>
                </div>
              )}

              <button
                  onClick={startCamera}
                  data-testid="start-verification-btn"
                  className="w-full py-2.5 sm:py-3 bg-amber-500 hover:bg-amber-400 active:bg-amber-600 text-zinc-900 font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <Camera className="w-4 h-4 sm:w-5 sm:h-5" />
                  Open Camera &amp; Start Verification
                </button>

              <button onClick={onSkip} className="w-full mt-2 py-2 text-zinc-500 hover:text-zinc-300 text-xs sm:text-sm transition-colors" data-testid="skip-verification-btn">
                Skip for now
              </button>
            </div>
          )}

          {/* Camera */}
          {step === 'camera' && (
            <div className="p-4 sm:p-6" data-testid="verification-camera">
              <div className="relative bg-black rounded-xl overflow-hidden mb-3 sm:mb-4" style={{ aspectRatio: '4/3' }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                  style={{ transform: 'scaleX(-1)' }}
                />
                {/* Face guide */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-32 h-40 sm:w-48 sm:h-60 border-2 border-amber-400/50 rounded-[40%] border-dashed" />
                </div>
                {!cameraReady && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                    <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
                  </div>
                )}
                <div className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3 right-2 sm:right-3 bg-black/60 backdrop-blur rounded-lg p-1.5 sm:p-2 text-center">
                  <p className="text-white text-[10px] sm:text-xs">Position your face within the guide. Ensure good lighting.</p>
                </div>
              </div>

              <button
                onClick={capturePhoto}
                disabled={!cameraReady}
                data-testid="capture-photo-btn"
                className="w-full py-2.5 sm:py-3 bg-white hover:bg-zinc-100 active:bg-zinc-200 text-zinc-900 font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Camera className="w-4 h-4 sm:w-5 sm:h-5" />
                Capture Photo
              </button>
            </div>
          )}

          {/* Preview */}
          {step === 'preview' && (
            <div className="p-4 sm:p-6" data-testid="verification-preview">
              <p className="text-zinc-400 text-xs sm:text-sm mb-2 sm:mb-3 text-center">Review your photo</p>
              <div className="bg-black rounded-xl overflow-hidden mb-3 sm:mb-4" style={{ aspectRatio: '4/3' }}>
                <img src={capturedPhoto} alt="Captured" className="w-full h-full object-cover" style={{ transform: 'scaleX(-1)' }} />
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-2.5 sm:p-3 mb-3 sm:mb-4">
                  <p className="text-red-400 text-xs sm:text-sm">{error}</p>
                </div>
              )}

              <div className="flex gap-2.5 sm:gap-3">
                <button
                  onClick={retakePhoto}
                  data-testid="retake-photo-btn"
                  className="flex-1 py-2.5 sm:py-3 bg-zinc-800 hover:bg-zinc-700 active:bg-zinc-600 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm"
                >
                  <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  Retake
                </button>
                <button
                  onClick={submitVerification}
                  data-testid="submit-verification-btn"
                  className="flex-1 py-2.5 sm:py-3 bg-amber-500 hover:bg-amber-400 active:bg-amber-600 text-zinc-900 font-semibold rounded-xl transition-colors flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm"
                >
                  <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  Verify Identity
                </button>
              </div>
            </div>
          )}

          {/* Verifying */}
          {step === 'verifying' && (
            <div className="p-8 sm:p-12 text-center" data-testid="verification-processing">
              <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 animate-spin text-amber-400 mx-auto mb-4" />
              <h3 className="text-white font-semibold text-base sm:text-lg mb-2">Verifying Identity</h3>
              <p className="text-zinc-500 text-xs sm:text-sm">Comparing your photo. This may take a few seconds...</p>
            </div>
          )}

          {/* Result */}
          {step === 'result' && verificationResult && (
            <div className="p-6 sm:p-8 text-center" data-testid="verification-result">
              {verificationResult.verified ? (
                <>
                  <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 bg-emerald-500/10 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-400" />
                  </div>
                  <h3 className="text-white font-bold text-lg sm:text-xl mb-2">Identity Verified</h3>
                  <p className="text-zinc-400 text-xs sm:text-sm mb-2">{verificationResult.message}</p>
                  <div className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-500/10 rounded-full">
                    <span className="text-emerald-400 text-xs font-medium">
                      Confidence: {Math.round(verificationResult.confidence * 100)}%
                    </span>
                  </div>
                  <p className="text-zinc-600 text-[10px] sm:text-xs mt-4">Redirecting to exam...</p>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 bg-red-500/10 rounded-full flex items-center justify-center">
                    <XCircle className="w-7 h-7 sm:w-8 sm:h-8 text-red-400" />
                  </div>
                  <h3 className="text-white font-bold text-lg sm:text-xl mb-2">Verification Failed</h3>
                  <p className="text-zinc-400 text-xs sm:text-sm mb-4">{verificationResult.message}</p>
                  {verificationResult.attempts_remaining > 0 ? (
                    <button
                      onClick={() => { setCapturedPhoto(null); setStep('camera'); setVerificationResult(null); }}
                      data-testid="retry-verification-btn"
                      className="px-5 sm:px-6 py-2 sm:py-2.5 bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold rounded-xl transition-colors text-sm"
                    >
                      Try Again ({verificationResult.attempts_remaining} left)
                    </button>
                  ) : (
                    <div className="bg-zinc-800/50 rounded-lg p-4 mt-4">
                      <AlertTriangle className="w-5 h-5 text-amber-400 mx-auto mb-2" />
                      <p className="text-zinc-400 text-xs sm:text-sm">Contact your exam organiser for manual verification.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Blocked */}
          {step === 'blocked' && (
            <div className="p-6 sm:p-8 text-center" data-testid="verification-blocked-step">
              <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 bg-red-500/10 rounded-full flex items-center justify-center">
                <XCircle className="w-7 h-7 sm:w-8 sm:h-8 text-red-400" />
              </div>
              <h3 className="text-white font-bold text-lg sm:text-xl mb-2">Verification Blocked</h3>
              <p className="text-zinc-400 text-xs sm:text-sm">
                All attempts used. Please contact your exam organiser for manual verification.
              </p>
            </div>
          )}
        </div>

        <canvas ref={canvasRef} className="hidden" />

        {/* Progress dots */}
        <div className="flex justify-center mt-3 sm:mt-4 gap-1.5 sm:gap-2">
          {['instructions', 'camera', 'preview', 'result'].map((s, i) => (
            <div
              key={s}
              className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-colors ${
                ['instructions', 'camera', 'preview', 'verifying', 'result'].indexOf(step) >= i ? 'bg-amber-400' : 'bg-zinc-700'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
